/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import "../add-project/addProject.css";
import {
  UilArchiveAlt,
  UilCheck,
  UilDownloadAlt,
  UilFolderOpen,
  UilImageUpload,
  UilImport,
  UilTrashAlt,
} from "@iconscout/react-unicons";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import axios from "axios";
import JSZip from "jszip";
import { resolveUrl } from "../../../utils/storeUtils";
import { Tooltip } from "react-tooltip";

const IMAGE_EXT =
  /\.(jpg|jpeg|png|gif|bmp|tiff|webp|svg|heic|heif|ico|avif)$/i;

/** Max uncompressed ZIP payload we read in the browser (approx. via file.size). */
const MAX_ZIP_BYTES = 80 * 1024 * 1024;
/** Max image entries taken from a single ZIP (avoids huge archives freezing the tab). */
const MAX_IMAGES_PER_ZIP = 250;

/** Written by server ZIP export; restores alt text when you re-upload the archive. */
const PARTNER_ZIP_MANIFEST = "partner-logos-manifest.json";

function isImageFile(file) {
  if (!file || !file.name) return false;
  if (file.type && file.type.startsWith("image/")) return true;
  return IMAGE_EXT.test(file.name);
}

function isZipFile(file) {
  if (!file?.name) return false;
  const t = (file.type || "").toLowerCase();
  if (t === "application/zip" || t === "application/x-zip-compressed") {
    return true;
  }
  return /\.zip$/i.test(file.name);
}

function isPartnerUploadEntry(file) {
  return isImageFile(file) || isZipFile(file);
}

function defaultAltFromFileName(name) {
  const base = String(name || "").replace(/\\/g, "/").split("/").pop() || "";
  return base.replace(IMAGE_EXT, "").replace(/[-_]+/g, " ").trim() || "Partner logo";
}

function readAllDirEntries(dirReader) {
  return new Promise((resolve, reject) => {
    const acc = [];
    const read = () => {
      dirReader.readEntries(
        (batch) => {
          if (batch.length) {
            acc.push(...batch);
            read();
          } else {
            resolve(acc);
          }
        },
        (err) => reject(err)
      );
    };
    read();
  });
}

async function walkFileTree(entry, fileAcc) {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
    fileAcc.push(file);
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllDirEntries(entry.createReader());
    await Promise.all(children.map((child) => walkFileTree(child, fileAcc)));
  }
}

/** Collect images and .zip files from a drop (multiple files, folder, or zips). */
async function getPartnerEntriesFromDataTransfer(dataTransfer) {
  const raw = [];
  const items = dataTransfer?.items;

  if (
    items &&
    items.length > 0 &&
    typeof items[0].webkitGetAsEntry === "function"
  ) {
    const tasks = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== "file") continue;
      const entry = item.webkitGetAsEntry();
      if (entry) tasks.push(walkFileTree(entry, raw));
    }
    await Promise.all(tasks);
  } else if (dataTransfer?.files?.length) {
    raw.push(...Array.from(dataTransfer.files));
  }

  return raw.filter(isPartnerUploadEntry);
}

/**
 * @typedef {{ file: File; alt?: string }} PartnerQueuedFile
 */

/**
 * Read alt map from our export manifest inside a ZIP (filename → alt).
 */
async function loadPartnerAltMapFromZip(zip) {
  const key = Object.keys(zip.files).find((k) => {
    const entry = zip.files[k];
    if (!entry || entry.dir) return false;
    const base = k.replace(/\\/g, "/").split("/").pop();
    return base === PARTNER_ZIP_MANIFEST;
  });
  if (!key) return {};
  try {
    const text = await zip.files[key].async("string");
    const data = JSON.parse(text);
    const list = data.entries || data.partners;
    if (!Array.isArray(list)) return {};
    const map = {};
    for (const row of list) {
      const fn = row.file || row.filename;
      if (fn && typeof row.alt === "string") map[fn] = row.alt;
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Turn a mixed list of images and ZIPs into a flat list of queue items.
 * @returns {Promise<PartnerQueuedFile[]>}
 */
async function expandPartnerUploadFiles(files) {
  /** @type {PartnerQueuedFile[]} */
  const out = [];
  for (const file of files) {
    if (isImageFile(file)) {
      out.push({ file });
      continue;
    }
    if (isZipFile(file)) {
      const fromZip = await extractImagesFromZip(file);
      if (fromZip.length === 0) {
        toast.warn(`No supported images found in “${file.name}”.`);
      }
      out.push(...fromZip);
    }
  }
  return out;
}

/**
 * @returns {Promise<PartnerQueuedFile[]>}
 */
async function extractImagesFromZip(file) {
  if (file.size > MAX_ZIP_BYTES) {
    toast.error("ZIP is too large (max 80 MB).");
    return [];
  }
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const altByFileName = await loadPartnerAltMapFromZip(zip);
  /** @type {PartnerQueuedFile[]} */
  const images = [];
  const paths = Object.keys(zip.files).sort();
  let truncated = false;

  for (const relPath of paths) {
    const entry = zip.files[relPath];
    if (!entry || entry.dir) continue;
    const lower = relPath.replace(/\\/g, "/").toLowerCase();
    if (lower.includes("__macosx/")) continue;
    const base = relPath.split("/").pop() || "";
    if (!base || base.startsWith(".") || base.toLowerCase() === ".ds_store") {
      continue;
    }
    if (base === PARTNER_ZIP_MANIFEST) continue;
    if (!IMAGE_EXT.test(base)) continue;
    if (images.length >= MAX_IMAGES_PER_ZIP) {
      truncated = true;
      break;
    }
    const blob = await entry.async("blob");
    const type =
      blob.type && blob.type.startsWith("image/")
        ? blob.type
        : "application/octet-stream";
    const queued = { file: new File([blob], base, { type }) };
    if (Object.prototype.hasOwnProperty.call(altByFileName, base)) {
      queued.alt = altByFileName[base];
    }
    images.push(queued);
  }

  if (truncated) {
    toast.info(
      `Only the first ${MAX_IMAGES_PER_ZIP} images from “${file.name}” were added.`
    );
  }

  return images;
}

let partnerRowId = 0;
function nextRowId() {
  partnerRowId += 1;
  return `p-${partnerRowId}-${Date.now()}`;
}

const AddPartner = () => {
  const [partnerQueue, setPartnerQueue] = useState([]);
  const queueRef = useRef(partnerQueue);
  queueRef.current = partnerQueue;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [partnersRefresh, setPartnersRefresh] = useState(0);
  const [partnerDropActive, setPartnerDropActive] = useState(false);
  const partnerDragDepth = useRef(0);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    return () => {
      queueRef.current.forEach((row) => {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl);
      });
    };
  }, []);

  const appendFiles = useCallback((/** @type {PartnerQueuedFile[]} */ items) => {
    if (!items.length) {
      setError(
        "No images found. Drop image files, a folder, or a .zip that contains images."
      );
      return;
    }
    setError("");
    setPartnerQueue((prev) => {
      const next = [...prev];
      for (const item of items) {
        const file = item.file;
        const fromManifest = item.alt;
        const alt =
          fromManifest !== undefined && fromManifest !== null
            ? String(fromManifest).trim() || defaultAltFromFileName(file.name)
            : defaultAltFromFileName(file.name);
        next.push({
          id: nextRowId(),
          file,
          alt,
          previewUrl: URL.createObjectURL(file),
        });
      }
      return next;
    });
  }, []);

  const handleDrop = async (e) => {
    e.preventDefault();
    partnerDragDepth.current = 0;
    setPartnerDropActive(false);
    try {
      const entries = await getPartnerEntriesFromDataTransfer(e.dataTransfer);
      const images = await expandPartnerUploadFiles(entries);
      appendFiles(images);
    } catch {
      setError(
        "Could not read those files. Try images, a folder, or a valid .zip archive."
      );
    }
  };

  const onPartnerDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    partnerDragDepth.current += 1;
    setPartnerDropActive(true);
  };

  const onPartnerDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    partnerDragDepth.current -= 1;
    if (partnerDragDepth.current <= 0) {
      partnerDragDepth.current = 0;
      setPartnerDropActive(false);
    }
  };

  const onPartnerDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const list = e.target.files;
    const input = e.target;
    if (!list?.length) return;
    void (async () => {
      try {
        const picked = Array.from(list).filter(isPartnerUploadEntry);
        const images = await expandPartnerUploadFiles(picked);
        appendFiles(images);
      } catch {
        setError(
          "Could not read those files. Try images, a folder, or a valid .zip archive."
        );
      } finally {
        input.value = "";
      }
    })();
  };

  const removeRow = (id) => {
    setPartnerQueue((prev) => {
      const row = prev.find((r) => r.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((r) => r.id !== id);
    });
  };

  const clearQueue = () => {
    setPartnerQueue((prev) => {
      prev.forEach((row) => {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl);
      });
      return [];
    });
  };

  const updateAlt = (id, alt) => {
    setPartnerQueue((prev) =>
      prev.map((r) => (r.id === id ? { ...r, alt } : r))
    );
  };

  const imageFormats = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tiff",
    "webp",
    "svg",
    "heic",
    "ico",
    "avif",
  ];

  const handleAddPartner = async (e) => {
    e.preventDefault();

    if (!partnerQueue.length) {
      return toast.error("Add at least one partner image.");
    }

    const missingAlt = partnerQueue.some((r) => !String(r.alt || "").trim());
    if (missingAlt) {
      return toast.error("Each image needs alt text (or use the default from the file name).");
    }

    try {
      setLoading(true);
      for (let i = 0; i < partnerQueue.length; i++) {
        const row = partnerQueue[i];
        const partnerData = new FormData();
        partnerData.append("image", row.file);
        partnerData.append("alt", row.alt.trim());
        await axios.post("/partners", partnerData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }
      toast.success(
        partnerQueue.length === 1
          ? "Partner added successfully!"
          : `${partnerQueue.length} partners added successfully!`
      );
      clearQueue();
      setPartnersRefresh((k) => k + 1);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className="addProject">
        <form className="container" onSubmit={handleAddPartner}>
          <div className="title">
            <div className="text">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="35"
                viewBox="0 0 34 35"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.3511 2.271C9.21636 2.271 8.43861 2.271 7.74444 2.45941C7.02601 2.65659 6.35707 3.00266 5.78108 3.47517C5.20509 3.94768 4.73492 4.53605 4.40111 5.20208C4.07952 5.84241 3.92794 6.606 3.70552 7.7195L2.82719 12.1083C2.67164 12.8777 2.68667 13.6718 2.87122 14.4347C3.05577 15.1977 3.40536 15.9109 3.89536 16.5241V20.4128C3.89536 23.0167 3.89536 25.0793 4.11211 26.6929C4.33594 28.3532 4.80627 29.6977 5.86594 30.7587C6.92561 31.8184 8.27003 32.2887 9.93178 32.5126C11.5454 32.7293 13.608 32.7293 16.2104 32.7293H17.7872C20.391 32.7293 22.4537 32.7293 24.0673 32.5126C25.7276 32.2887 27.072 31.8184 28.1331 30.7587C29.1928 29.6977 29.6631 28.3532 29.8869 26.6929C30.1037 25.0779 30.1037 23.0167 30.1037 20.4128V16.5255C30.5937 15.912 30.9431 15.1985 31.1274 14.4353C31.3117 13.6721 31.3264 12.8778 31.1704 12.1083L30.2921 7.7195C30.0711 6.606 29.9181 5.84241 29.5979 5.20208C29.264 4.5359 28.7936 3.94744 28.2173 3.47492C27.6411 3.00241 26.9719 2.65641 26.2532 2.45941C25.5604 2.271 24.7827 2.271 23.6465 2.271H10.3511ZM25.8792 18.5627C26.6002 18.564 27.3142 18.4204 27.9787 18.1405V20.3335C27.9787 23.0351 27.9759 24.9547 27.7804 26.411C27.5891 27.8347 27.2293 28.6564 26.63 29.2557C26.0308 29.8549 25.2091 30.2147 23.7825 30.406C23.0593 30.4948 22.3321 30.5487 21.6037 30.5675V26.6632C21.6037 26.0398 21.6037 25.5015 21.564 25.0623C21.5215 24.5948 21.428 24.133 21.1759 23.6981C20.8964 23.2134 20.4943 22.8108 20.0099 22.5307C19.575 22.28 19.1132 22.1865 18.6457 22.144C18.2065 22.1043 17.6682 22.1043 17.0449 22.1043H16.9528C16.3294 22.1043 15.7925 22.1043 15.3519 22.144C14.8844 22.1865 14.424 22.28 13.9891 22.5307C13.5042 22.8106 13.1016 23.2132 12.8218 23.6981C12.571 24.133 12.4775 24.5948 12.435 25.0609C12.3954 25.5029 12.3954 26.0398 12.3954 26.6617V30.5675C11.666 30.5488 10.9379 30.4949 10.2137 30.406C8.78994 30.2147 7.96828 29.8549 7.36903 29.2557C6.76977 28.6564 6.40994 27.8347 6.21869 26.4096C6.02177 24.9547 6.02036 23.0351 6.02036 20.3335V18.1405C7.15254 18.618 8.41376 18.6949 9.59557 18.3585C10.7774 18.0221 11.809 17.2925 12.52 16.2903C13.034 16.9961 13.708 17.57 14.4866 17.965C15.2653 18.36 16.1264 18.5648 16.9995 18.5627C17.8726 18.5648 18.7338 18.36 19.5124 17.965C20.2911 17.57 20.965 16.9961 21.479 16.2903C21.977 16.9936 22.6366 17.5671 23.4023 17.9625C24.168 18.3579 25.0174 18.5637 25.8792 18.5627ZM14.5204 30.6015C15.07 30.6034 15.6603 30.6043 16.2912 30.6043H17.7079C18.3387 30.6043 18.929 30.6034 19.4787 30.6015V26.7085C19.4787 26.0271 19.4773 25.5865 19.4475 25.2536C19.4192 24.9348 19.3696 24.8187 19.3356 24.7606C19.2425 24.5994 19.1086 24.4656 18.9474 24.3724C18.8894 24.3384 18.7732 24.2902 18.4544 24.2605C17.9701 24.2309 17.4847 24.2205 16.9995 24.2293C16.3181 24.2293 15.8775 24.2293 15.5446 24.2605C15.2244 24.2888 15.1097 24.3384 15.0516 24.3724C14.8899 24.4654 14.7556 24.5992 14.662 24.7606C14.6294 24.8172 14.5799 24.9348 14.5515 25.2536C14.5218 25.5865 14.5204 26.0271 14.5204 26.7085V30.6015ZM12.282 4.396H10.5084C9.15686 4.396 8.68794 4.40591 8.30827 4.50933C7.87729 4.62754 7.47598 4.83505 7.13039 5.11841C6.7848 5.40176 6.50266 5.75462 6.30227 6.15408C6.12519 6.50683 6.02461 6.963 5.75969 8.289L4.91252 12.5248C4.82224 12.9607 4.8218 13.4103 4.91125 13.8463C5.00069 14.2823 5.17814 14.6955 5.43273 15.0606C5.68732 15.4257 6.01371 15.735 6.39193 15.9696C6.77016 16.2042 7.19226 16.3592 7.63244 16.4251C8.07262 16.491 8.52161 16.4664 8.95197 16.3529C9.38232 16.2393 9.78498 16.0391 10.1353 15.7646C10.4857 15.4901 10.7763 15.147 10.9896 14.7563C11.2028 14.3657 11.334 13.9356 11.3754 13.4924L11.4731 12.5206L11.4788 12.4611L12.282 4.396ZM13.5868 12.7189L14.4198 4.396H19.5793L20.4066 12.6722C20.453 13.1457 20.4001 13.6237 20.2515 14.0757C20.1028 14.5277 19.8615 14.9438 19.5431 15.2973C19.2247 15.6509 18.836 15.9342 18.402 16.1292C17.968 16.3242 17.4981 16.4265 17.0223 16.4298C16.5465 16.4331 16.0752 16.3371 15.6386 16.1481C15.2019 15.9591 14.8094 15.6811 14.4862 15.332C14.1629 14.9828 13.916 14.5701 13.7612 14.1202C13.6063 13.6703 13.5469 13.1931 13.5868 12.7189ZM25.6908 4.50933C25.3111 4.40591 24.8422 4.396 23.4907 4.396H21.7142L22.6251 13.491C22.6678 13.9329 22.7999 14.3615 23.0135 14.7506C23.2271 15.1398 23.5177 15.4814 23.8676 15.7547C24.2174 16.028 24.6193 16.2271 25.0486 16.3401C25.4779 16.4531 25.9257 16.4775 26.3648 16.4119C26.8039 16.3462 27.225 16.1919 27.6025 15.9582C27.98 15.7246 28.306 15.4166 28.5606 15.053C28.8152 14.6893 28.9932 14.2776 29.0836 13.843C29.174 13.4084 29.175 12.9599 29.0865 12.5248L28.2408 8.289C27.9744 6.963 27.8739 6.50683 27.6982 6.15408C27.4977 5.75447 27.2153 5.40152 26.8695 5.11816C26.5236 4.8348 26.122 4.62736 25.6908 4.50933Z"
                  fill="black"
                  fillOpacity={0.6}
                />
              </svg>
              <p>Add New Partner</p>
            </div>
            <button type="submit" disabled={loading}>
              <UilCheck size={22} />
              {partnerQueue.length > 1
                ? `Add ${partnerQueue.length} partners`
                : "Add Partner"}
            </button>
          </div>
          <div className="form">
            <div className="image partner-upload-panel" style={{ width: "100%" }}>
              <div className="partner-upload-panel__head">
                <h3>Partner logos</h3>
                <p className="partner-upload-panel__lead">
                  Add one or many logos at once: single images, a whole folder,
                  or a <strong>.zip</strong> that contains images. Alt text is
                  suggested from each file name.
                </p>
              </div>

              <div
                className={`imgInput partner-upload-dropzone${
                  partnerQueue.length ? " partner-drop-more" : ""
                }${partnerDropActive ? " partner-upload-dropzone--active" : ""}`}
                onDragEnter={onPartnerDragEnter}
                onDragLeave={onPartnerDragLeave}
                onDragOver={onPartnerDragOver}
                onDrop={handleDrop}
              >
                <div className="partner-upload-dropzone__inner">
                  <div className="partner-upload-dropzone__icon" aria-hidden>
                    <UilImageUpload size={32} />
                  </div>
                  <div className="partner-upload-dropzone__copy">
                    <p className="partner-upload-dropzone__title">
                      {partnerQueue.length
                        ? "Add more logos"
                        : "Drag & drop images or a .zip here"}
                    </p>
                    <p className="partner-upload-dropzone__hint">
                      {partnerQueue.length
                        ? "Drop more images, a folder, or a .zip—they will join the list below."
                        : "Or browse below. ZIP files are unpacked in the browser; folders work best in Chrome or Edge."}
                    </p>
                  </div>
                  <div className="partner-upload-dropzone__actions">
                    <label className="partner-upload-dropzone__pick partner-upload-dropzone__pick--primary">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileInputChange}
                        accept="image/*,.heic,.heif,.zip,application/zip"
                      />
                      <span className="partner-upload-dropzone__pick-inner">
                        <UilImport size={18} />
                        Browse files
                      </span>
                    </label>
                    <label className="partner-upload-dropzone__pick partner-upload-dropzone__pick--secondary">
                      <input
                        type="file"
                        multiple
                        accept=".zip,application/zip"
                        onChange={handleFileInputChange}
                      />
                      <span className="partner-upload-dropzone__pick-inner">
                        <UilArchiveAlt size={18} />
                        Browse ZIP
                      </span>
                    </label>
                    <label className="partner-upload-dropzone__pick partner-upload-dropzone__pick--secondary">
                      <input
                        type="file"
                        multiple
                        webkitdirectory="true"
                        onChange={handleFileInputChange}
                      />
                      <span className="partner-upload-dropzone__pick-inner">
                        <UilFolderOpen size={18} />
                        Browse folder
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {error && <p className="error">{error}</p>}

              <div className="image-p partner-upload-formats">
                <p className="inf" id="inf">
                  Supported formats
                </p>
                <i className="uil uil-info-circle" id="formats"></i>
              </div>
              <Tooltip
                anchorSelect="#formats"
                content={`${imageFormats.join(", ")}. ZIP: max 80 MB, up to ${MAX_IMAGES_PER_ZIP} images. Archives from “Download all (ZIP)” include partner-logos-manifest.json so alt text is restored when you re-upload.`}
                place="top"
              />

              {partnerQueue.length > 0 && (
                <div className="partner-queue">
                  {partnerQueue.map((row) => (
                    <div className="partner-queue-row" key={row.id}>
                      <div className="partner-queue-thumb">
                        <img src={row.previewUrl} alt="" />
                      </div>
                      <div className="partner-queue-alt">
                        <label htmlFor={`alt-${row.id}`}>Image alt</label>
                        <input
                          id={`alt-${row.id}`}
                          value={row.alt}
                          onChange={(ev) => updateAlt(row.id, ev.target.value)}
                          type="text"
                          placeholder="Short description for accessibility"
                        />
                      </div>
                      <button
                        type="button"
                        className="partner-queue-remove"
                        onClick={() => removeRow(row.id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
        <Partners refreshTrigger={partnersRefresh} />
      </main>
    </>
  );
};

function partnerImageDownloadName(partner, absoluteUrl) {
  const fromPath = decodeURIComponent(
    String(partner?.image?.url || "")
      .split(/[/\\]/)
      .pop() || ""
  );
  if (fromPath && /\.[a-z0-9]{2,4}$/i.test(fromPath)) return fromPath;
  try {
    const u = new URL(absoluteUrl, window.location.origin);
    const last = decodeURIComponent(u.pathname.split("/").pop() || "");
    if (last) return last;
  } catch {
    /* ignore */
  }
  const alt = String(partner?.image?.alt || "partner-logo")
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  return `${alt || "partner-image"}.jpg`;
}

async function downloadSinglePartnerImage(partner, apiBaseUrl) {
  const raw = partner?.image?.url;
  if (!raw) {
    toast.error("No image URL for this partner.");
    return;
  }
  const url = resolveUrl(raw, apiBaseUrl);
  const filename = partnerImageDownloadName(partner, url);

  try {
    const res = await fetch(url, { credentials: "include", mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.info("Opened image in a new tab if download did not start.");
  }
}

function Partners({ refreshTrigger = 0 }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zipDownloading, setZipDownloading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const apiBaseUrl = axios.defaults.baseURL || "";

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/partners");
        setPartners(response?.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [refreshTrigger]);

  const handleDelete = async (partnerId) => {
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      await axios.delete(`/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners(partners.filter((partner) => partner._id !== partnerId));
      toast.success("Partner has been deleted..!");
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while deleting."
      );
    }
  };

  const handleDownloadAllZip = async () => {
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setZipDownloading(true);
      const res = await axios.get("/partners/all-images.zip", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = res.data;
      const ct = String(res.headers["content-type"] || "").toLowerCase();
      if (blob instanceof Blob && ct.includes("application/json")) {
        const text = await blob.text();
        const j = JSON.parse(text);
        toast.error(j.message || "Download failed.");
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `partner-logos-${stamp}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Partner logos archive downloaded.");
    } catch (error) {
      let msg = "Could not download the archive.";
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const t = await data.text();
          const j = JSON.parse(t);
          if (j.message) msg = j.message;
        } catch {
          /* keep default */
        }
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      }
      toast.error(msg);
    } finally {
      setZipDownloading(false);
    }
  };

  const handleDeleteAllPartners = async () => {
    const count = partners.length;
    if (count === 0) return;
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    const confirmed = window.confirm(
      `Delete all ${count} partner logo${count === 1 ? "" : "s"}?\n\n` +
        "Image files on the server will be removed. This cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeleteAllLoading(true);
      await axios.delete("/partners/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners([]);
      toast.success("All partner logos have been removed.");
    } catch (error) {
      console.error("Error deleting all partners:", error);
      toast.error(
        error.response?.data?.message || "Could not delete all partners."
      );
    } finally {
      setDeleteAllLoading(false);
    }
  };

  return (
    <div className="partners partners-dashboard">
      <div className="container">
        {partners?.length > 0 && (
          <div className="partners-dashboard-toolbar">
            <h2 className="partners-dashboard-title">Saved partner logos</h2>
            <div className="partners-dashboard-toolbar-actions">
              <button
                type="button"
                className="partners-download-all-btn"
                onClick={handleDownloadAllZip}
                disabled={zipDownloading || deleteAllLoading}
                title="Download every partner logo in one ZIP file"
              >
                <UilDownloadAlt size={18} />
                {zipDownloading ? "Preparing…" : "Download all (ZIP)"}
              </button>
              <button
                type="button"
                className="partners-delete-all-btn"
                onClick={handleDeleteAllPartners}
                disabled={zipDownloading || deleteAllLoading}
                title="Remove every partner logo from the database and server"
              >
                <UilTrashAlt size={18} />
                {deleteAllLoading ? "Deleting…" : "Delete all"}
              </button>
            </div>
          </div>
        )}
        <div className="boxes">
          {partners?.map((partner) => {
            const label = String(partner?.image?.alt || "").trim();
            return (
              <article className="box partner-card" key={partner?._id}>
                <div className="partner-card-media">
                  <img
                    src={resolveUrl(partner?.image?.url, apiBaseUrl)}
                    alt={partner?.image?.alt || "Partner logo"}
                  />
                </div>
                {label ? (
                  <p className="partner-card-label" title={label}>
                    {label}
                  </p>
                ) : (
                  <p className="partner-card-label partner-card-label--muted">
                    No label
                  </p>
                )}
                <div className="partner-box-actions">
                  <button
                    type="button"
                    className="partner-icon-btn partner-icon-btn--download"
                    onClick={() =>
                      downloadSinglePartnerImage(partner, apiBaseUrl)
                    }
                    title="Download this logo"
                    aria-label="Download partner logo"
                  >
                    <UilDownloadAlt size={20} />
                  </button>
                  <button
                    type="button"
                    className="partner-icon-btn partner-icon-btn--delete"
                    onClick={() => handleDelete(partner?._id)}
                    title="Remove partner"
                    aria-label="Delete partner"
                  >
                    <UilTrashAlt size={20} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default AddPartner;
